import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, MessageSquare } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const ReviewsSection = ({ userId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [averageRating, setAverageRating] = useState(0);

  useEffect(() => {
    fetchReviews();
  }, [userId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/brands/${userId}/reviews`);

      setReviews(response.data.reviews || []);
      setAverageRating(response.data.averageRating || 0);
    } catch (err) {
      let errorMessage = 'Failed to load reviews';
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'Brand not found';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const averageRatingDisplay = averageRating.toFixed(1);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Reviews & Ratings</h2>
        <p className="section-description">See what creators say about your brand</p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading reviews...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Rating Summary */}
          <div className="rating-summary">
            <div className="rating-overview">
              <div className="average-rating">
                <span className="rating-number">{averageRatingDisplay}</span>
                <div className="rating-stars">
                  {renderStars(Math.round(averageRating))}
                </div>
                <p className="rating-count">Based on {reviews.length} reviews</p>
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="reviews-list">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div>
                    <p className="review-creator">{review.creator}</p>
                    <p className="review-campaign">{review.campaign}</p>
                  </div>
                  <div className="review-meta">
                    <div className="review-stars">{renderStars(review.rating)}</div>
                    <p className="review-date">{new Date(review.date).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="review-body">
                  <p className="review-comment">{review.comment}</p>
                </div>

                <div className="review-footer">
                  <button className="review-action">
                    <ThumbsUp size={16} />
                    <span>Helpful ({review.helpful})</span>
                  </button>
                  <button className="review-action">
                    <MessageSquare size={16} />
                    <span>Reply</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {reviews.length === 0 && (
            <div className="empty-state">
              <Star size={48} className="text-gray-400 mb-4" />
              <p className="empty-state-text">No reviews yet</p>
              <p className="text-gray-500 text-sm">Reviews from creators will appear here</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsSection;


